import type { PortfolioSettings, Policy, Posture, Regime, Band } from './types.js';

/**
 * The default policy — every limit the app enforces, in one editable object.
 *
 * Engines read `settings.policy`, never a literal, so changing a number here
 * changes every flag, policy check, playbook gate and verdict that depends on it.
 */
export const DEFAULT_POLICY: Policy = {
  maxStressDrawdown: 0.15,
  maxPositionWeight: 0.15,
  maxSectorWeight: 0.35,
  maxGross: 1.5,
  volTargetDaily: 0.005,
  volTolerance: 1.25,
  maxClusterWeight: 0.35,
  maxTop3RiskShare: 0.6,
  maxHedgeShare: 0.2,
  // "Never recommend hedges that cost > ~1.5%/yr of book without flagging
  // long vol donation" — SPEC.md.
  maxHedgeAnnualCost: 0.015,
  // 0.70 only catches near-duplicate holdings; 0.45 catches the groups that
  // actually converge when risk comes off, which is where the surprise lives.
  clusterThresholds: { hard: 0.7, soft: 0.45 },
};

/** Model and behaviour settings. Limits live in `policy`. */
export const DEFAULT_SETTINGS: PortfolioSettings = {
  equity: null,
  policy: DEFAULT_POLICY,
  hedgeOtmPct: 0.05,
  hedgeExpiryDays: 45,
  riskFreeRate: 0.04,
  betaEstimationBand: 0.2,
  vixThresholds: { low: 15, high: 23 },
  // Two components must agree before posture leaves neutral: at ±1 a single
  // component could flip the bands on its own, which is exactly the fragility
  // a composite is supposed to remove.
  postureThreshold: 2,
  postureCooldownHours: 12,
  // Advisory by default: posture is shown, but the regime band is what binds.
  bindPosture: false,
  bands: {
    low: { min: 0.8, max: 1.2 },
    moderate: { min: 0.5, max: 1.0 },
    high: { min: 0.2, max: 0.6 },
  },
};

/** Exposure multiplier is clamped so a dead-quiet tape can't suggest 6x leverage. */
export const EXPOSURE_MULTIPLIER_CAP = { min: 0.25, max: 2.0 };

/** Minimum overlapping return observations before we trust a computed beta. */
export const MIN_BETA_OBSERVATIONS = 40;

/**
 * Raw OLS betas are clamped before shrinkage. A one-year regression on a thin or
 * news-driven name can print 5+ or deeply negative; those are estimation
 * artifacts, not risk, and they would otherwise dominate beta-adjusted net.
 */
export const BETA_CAP = { min: -1, max: 3 };

/**
 * Blume shrinkage: betas mean-revert toward 1 out of sample, so the raw estimate
 * is pulled a third of the way there before it is used in any risk number.
 */
export const BLUME = { slope: 0.67, intercept: 0.33 };

export function blumeAdjust(rawBeta: number): number {
  return BLUME.slope * rawBeta + BLUME.intercept;
}

export const BENCHMARK_TICKER = 'SPY';
export const VIX_TICKER = '^VIX';

/** Index hedge candidates; the recommender picks whichever tracks the book best. */
export const HEDGE_INDEX_TICKERS = ['SPY', 'QQQ'];

/** 3-month VIX, for the term-structure signal. */
export const VIX3M_TICKER = '^VIX3M';

/** Credit-spread proxy: high yield relative to investment grade. */
export const CREDIT_TICKERS = { highYield: 'HYG', investmentGrade: 'LQD' };

/** Trend is measured on these, both of which are already fetched as hedge candidates. */
export const TREND_TICKERS = ['SPY', 'QQQ'];

/**
 * Universe for the computed breadth fallback.
 *
 * Deliberately ~85 names rather than the whole bundled map: each one costs a
 * history subrequest, and Workers cap those. A broad, liquid, sector-spread
 * sample tracks a 500-name calculation closely enough for the purpose of
 * "is participation broad or narrow", and the reading carries its own
 * `universeSize` so the sample is never mistaken for the index.
 */
export const BREADTH_UNIVERSE = [
  'AAPL', 'MSFT', 'NVDA', 'AVGO', 'AMD', 'QCOM', 'TXN', 'MU', 'AMAT', 'CRM',
  'ORCL', 'ADBE', 'ACN', 'CSCO', 'IBM', 'INTU', 'NOW', 'PANW',
  'GOOGL', 'META', 'NFLX', 'DIS', 'CMCSA', 'TMUS', 'VZ',
  'AMZN', 'TSLA', 'HD', 'MCD', 'NKE', 'LOW', 'SBUX', 'BKNG', 'TJX',
  'WMT', 'COST', 'PG', 'KO', 'PEP', 'PM', 'MDLZ',
  'LLY', 'UNH', 'JNJ', 'ABBV', 'MRK', 'PFE', 'TMO', 'ABT', 'AMGN',
  'JPM', 'V', 'MA', 'BAC', 'WFC', 'GS', 'BLK', 'AXP', 'SPGI',
  'GE', 'CAT', 'HON', 'UNP', 'RTX', 'LMT', 'DE', 'UPS',
  'XOM', 'CVX', 'COP', 'EOG', 'SLB',
  'LIN', 'APD', 'SHW', 'FCX', 'NEM',
  'PLD', 'AMT', 'EQIX', 'SPG', 'O',
  'NEE', 'DUK', 'SO', 'D', 'AEP',
];

/** Posture component thresholds. All configurable-by-editing, per SPEC's defaults style. */
export const POSTURE_THRESHOLDS = {
  /** spot/3M above this is backwardation — near-term stress bid. */
  vixBackwardation: 1.0,
  /** spot/3M below this is healthy contango. */
  vixContango: 0.95,
  breadthStrong: 0.6,
  breadthWeak: 0.4,
  newHighsStrong: 0.05,
  newHighsWeak: -0.05,
  /** HYG/LQD within this fraction of its 50dma counts as neutral. */
  creditNeutralBand: 0.0025,
} as const;

export const MOVING_AVERAGES = { fast: 50, slow: 200 } as const;

export const POSTURE_LABELS: Record<Posture, string> = {
  press: 'PRESS',
  neutral: 'NEUTRAL',
  release: 'RELEASE',
};

export const REGIME_LABELS: Record<Regime, string> = {
  low: 'LOW VOL',
  moderate: 'MODERATE',
  high: 'HIGH VOL',
};

export function mergeSettings(overrides?: Partial<PortfolioSettings>): PortfolioSettings {
  if (!overrides) return { ...DEFAULT_SETTINGS };
  return {
    ...DEFAULT_SETTINGS,
    ...overrides,
    vixThresholds: { ...DEFAULT_SETTINGS.vixThresholds, ...overrides.vixThresholds },
    policy: {
      ...DEFAULT_POLICY,
      ...overrides.policy,
      clusterThresholds: { ...DEFAULT_POLICY.clusterThresholds, ...overrides.policy?.clusterThresholds },
    },
    bands: { ...DEFAULT_SETTINGS.bands, ...overrides.bands } as Record<Regime, Band>,
  };
}
